import { type VariantProps, cv } from "@suse-o11y/class-variance";
import { omit } from "@suse-o11y/omit";
import { on } from "@suse-o11y/on";
import { Tuple } from "@suse-o11y/tuple";
import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";

import { handleClipboardOverride } from "./_clipboard";
import styles from "./_truncated.module.css";

const DEFAULT_ELEMENT = "span" satisfies React.ElementType;
const NON_BREAKING_SPACE = "\u00A0";

const TRAILING_SPACE_REGEX = / $/u;
const LEADING_SPACE_REGEX = /^ /u;

on("copy", (doc, event) => handleClipboardOverride(`.${styles.truncated}:has(.${styles.rightHalf})`, doc, event), {
	throttle: 0,
});

function splitText(text: string) {
	/**
	 * Cut the text into halves.
	 * The left half is rendered with break-all but always as a single line,
	 * so the overflowed content of the left side is hidden.
	 * The right half uses `dir="rtl"` to make the truncation happen at the left side of the right half,
	 * so it appears as if the truncation happens in the middle of the whole text.
	 *
	 * Replace spaces at the edges with non-breaking spaces to prevent "gluing" words together if the middle is a space char.
	 */
	const left = text.slice(0, (text.length + 1) / 2).replace(TRAILING_SPACE_REGEX, NON_BREAKING_SPACE);
	const right = text.slice(left.length).replace(LEADING_SPACE_REGEX, NON_BREAKING_SPACE);

	return Tuple(left, right);
}

function isTruncated(element: null | Node) {
	return element instanceof HTMLElement && element.offsetWidth < element.scrollWidth;
}

const variants = cv(styles.truncated, {
	variants: {
		inline: {
			true: styles.inline,
		},
	},

	defaultVariants: {
		inline: false,
	},
});

interface TruncatedOwnProps extends VariantProps<typeof variants>, React.HTMLAttributes<HTMLElement> {
	/**
	 * The custom tooltip content.
	 * Shows the full `children` text when truncated if set to `true`.
	 * Does not show any tooltip if set to `false`.
	 * If not provided, the `children` will be used for the truncated text.
	 *
	 * @default true
	 */
	tooltip?: React.ReactNode;
}

interface TruncatedMiddleProps {
	truncate: "middle";
	children: number | string;
}

interface TruncatedEndProps {
	truncate?: "end";
	children: React.ReactNode;
}

type TruncatedProps<TElement extends React.ElementType = typeof DEFAULT_ELEMENT> = PolymorphicBoxProps<
	TElement,
	TruncatedOwnProps & (TruncatedMiddleProps | TruncatedEndProps)
>;

const Truncated = React.memo(
	React.forwardRef(
		(
			{ el: Component = DEFAULT_ELEMENT, className, inline, tooltip = true, onMouseEnter, ...props }: TruncatedProps,
			ref: React.ForwardedRef<HTMLElement>,
		) => {
			/**
			 * Lazy render the tooltip only when the text is actually truncated and hovered.
			 */
			const [hasTooltip, setHasTooltip] = React.useState(false);

			const [left, right] =
				props.truncate === "middle" ? splitText(props.children.toString()) : Tuple(props.children, "");

			return (
				<Tooltip hide={!hasTooltip || tooltip === false}>
					<TooltipTrigger>
						<Component
							className={variants({ inline, className })}
							onMouseEnter={(event) => {
								/**
								 * Show the tooltip only when the text is actually truncated if `tooltip` is `true` (default).
								 */
								if (tooltip === true) {
									/**
									 * If the .rightHalf is present - the middle truncation is active, otherwise it is end truncation.
									 *
									 * Use the imperative DOM API to select the right half node and check if it's truncated.
									 * It is done to cut down on the hooks usage because this component is used very often.
									 */
									setHasTooltip(
										isTruncated(
											event.currentTarget.lastChild instanceof HTMLElement
												? event.currentTarget.lastChild
												: event.currentTarget,
										),
									);
								} else if (tooltip) {
									/**
									 * Otherwise always show the tooltip if `tooltip` is truthy.
									 */
									setHasTooltip(true);
								}

								onMouseEnter?.(event);
							}}
							ref={ref}
							{...omit(props, ["truncate"])}
						>
							{left}
							{right.length > 0 && (
								<span className={styles.rightHalf} dir="rtl">
									{right}
								</span>
							)}
						</Component>
					</TooltipTrigger>

					<TooltipContent>{tooltip === true ? props.children : (tooltip ?? props.children)}</TooltipContent>
				</Tooltip>
			);
		},
	),
) as {
	<TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(props: TruncatedProps<TElement>): React.ReactElement;

	displayName?: string;
};

Truncated.displayName = "Truncated";

export { Truncated, type TruncatedProps };
